import React from "react";
import {
  Box,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const enterRow = keyframes`
  0%, 10% { transform: translateX(-18px); opacity: 0.2; }
  35%, 80% { transform: translateX(0); opacity: 1; }
  100% { transform: translateX(0); opacity: 0.2; }
`;

const ApplyingLoading = ({
  rowCount = 0,
  mode = "add",
  itemLabel = "row",
  itemLabelPlural = `${itemLabel}s`,
  targetLabel = "schedule",
}) => {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const items =
    rowCount > 0
      ? `${rowCount} ${rowCount === 1 ? itemLabel : itemLabelPlural}`
      : `your ${itemLabelPlural}`;

  return (
    <Stack
      role='status'
      aria-live='polite'
      aria-atomic='true'
      spacing={2}
      alignItems='center'
      sx={{ minWidth: 0, px: 1, py: { xs: 2, sm: 3 }, textAlign: "center" }}
    >
      <Box
        aria-hidden='true'
        sx={{
          width: 184,
          height: 144,
          flexShrink: 0,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper"
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
          sx={{
            px: 1.5,
            py: 1,
            color: "primary.main",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06)
          }}
        >
          <PlaylistAddCheckOutlinedIcon sx={{ fontSize: 24 }} />
          <Box
            sx={{ height: 5, width: 80, bgcolor: "divider", borderRadius: 1 }}
          />
        </Stack>
        <Stack spacing={1} sx={{ px: 1.5, py: 1.5 }}>
          {[0, 1, 2].map((row) => (
            <Stack
              key={row}
              direction='row'
              alignItems='center'
              spacing={1}
              sx={{
                animation: reduceMotion
                  ? "none"
                  : `${enterRow} 2.4s ease-in-out infinite`,
                animationDelay: `${row * 0.25}s`
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Box
                sx={{
                  height: 5,
                  width: row === 1 ? 60 : 76,
                  bgcolor: "divider",
                  borderRadius: 1
                }}
              />
              <Box
                sx={{
                  height: 5,
                  flex: 1,
                  bgcolor: "primary.main",
                  borderRadius: 1
                }}
              />
            </Stack>
          ))}
        </Stack>
      </Box>
      <Box sx={{ maxWidth: 420 }}>
        <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
          Applying {itemLabelPlural}
        </Typography>
        <Typography
          color='text.secondary'
          sx={{ fontSize: 16, lineHeight: 1.5 }}
        >
          {mode === "replace"
            ? `Replacing the current ${targetLabel} with ${items}.`
            : `Adding ${items} to your ${targetLabel}.`}
        </Typography>
      </Box>
      <LinearProgress
        aria-label={`Applying ${itemLabelPlural}`}
        sx={{
          width: 168,
          height: 4,
          borderRadius: 1,
          ...(reduceMotion && {
            "& .MuiLinearProgress-bar": {
              animation: "none",
              transform: "none",
              width: "40%"
            }
          })
        }}
      />
    </Stack>
  );
};

export default ApplyingLoading;
